import { formatDate } from "@/core/helpers/date";
import type { PricelistPeriod } from "@/generated/graphql";
import { Route as DetailRoute } from "@/routes/admin/pricelist/period/$id/detail";
import { Route as UpdateRoute } from "@/routes/admin/pricelist/period/$id/update";
import {
	Badge,
	Button,
	Can,
	DataTable,
	type DataTableFiltersProp,
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
	useDialogController,
} from "@k3-tech/react-kit";
import { Link } from "@tanstack/react-router";
import type { ColumnDef, PaginationState } from "@tanstack/react-table";
import { Edit, EllipsisIcon, EyeIcon } from "lucide-react";
import { useCallback, useMemo } from "react";

export interface PricelistPeriodListProps {
	pricelistID?: string;
	data: Partial<PricelistPeriod>[];
	loading: boolean;
	onRefresh: () => void;
	onDelete: (data: Partial<PricelistPeriod>) => void;
	onFormFilterChange?: (values: Record<string, unknown>) => void;
	paginationState: PaginationState;
	onPaginationChange?: (paginationState: PaginationState) => void;
	rowCount?: number | null;
}

export const PricelistPeriodList = ({
	pricelistID,
	data,
	loading,
	onRefresh,
	onDelete,
	onFormFilterChange,
	paginationState,
	onPaginationChange,
	rowCount,
}: PricelistPeriodListProps) => {
	const { confirm } = useDialogController();

	const selfOnDelete = useCallback(
		async (data: Partial<PricelistPeriod>) => {
			const ok = await confirm({
				title: `Delete ${data.pricelistPeriodName}?`,
				description: "This action cannot be undone.",
				destructive: true,
				confirmText: "Delete",
				cancelText: "Cancel",
			});
			if (!ok) return;
			await onDelete(data);
		},
		[confirm, onDelete],
	);

	const columns: ColumnDef<Partial<PricelistPeriod>, unknown>[] = useMemo(
		() => [
			{
				id: "pricelistPeriodName",
				header: "Period Name",
				accessorKey: "pricelistPeriodName",
			},
			{
				id: "effectiveStartDate",
				header: "Start Date",
				accessorKey: "effectiveStartDate",
				cell: ({ getValue }) => formatDate(getValue() as string),
			},
			{
				id: "effectiveEndDate",
				header: "End Date",
				accessorKey: "effectiveEndDate",
				cell: ({ getValue }) => formatDate(getValue() as string),
			},
			{
				id: "expiry",
				header: "Expiry Status",
				accessorKey: "effectiveEndDate",
				cell: ({ row }) =>
					new Date(row.original.effectiveEndDate).getTime() < Date.now()
						? "Expired"
						: "Not Expired",
			},
			{
				id: "isActive",
				header: "Status",
				accessorKey: "isActive",
				cell: ({ row }) => (
					<Badge
						variant={row.original.isActive ? "secondary" : "secondary"}
						className={row.original.isActive ? "text-green-600" : undefined}
					>
						{row.original.isActive ? "Active" : "Inactive"}
					</Badge>
				),
			},
			{
				id: "actions",
				header: "Actions",
				cell: ({ row }) => (
					<div className="flex gap-2">
						{/* Detail */}
						<Button variant="ghost" size="sm" className="h-8 w-8 p-0">
							<Link
								to={DetailRoute.fullPath}
								params={{ id: row.original.pricelistPeriodID }}
								search={{ pricelistID }}
							>
								<EyeIcon className="h-4 w-4" />
							</Link>
						</Button>

						{/* Edit */}
						<Can permissions="pricelist.edit">
							<Button variant="ghost" size="sm" className="h-8 w-8 p-0">
								<Link
									to={UpdateRoute.fullPath}
									params={{ id: row.original.pricelistPeriodID }}
									search={{ pricelistID }}
								>
									<Edit className="h-4 w-4" />
								</Link>
							</Button>
						</Can>

						<Can permissions="pricelist.delete">
							<DropdownMenu>
								<DropdownMenuTrigger asChild>
									<Button variant="outline" size="sm" className="h-8 w-8 p-0">
										<EllipsisIcon className="h-4 w-4" />
									</Button>
								</DropdownMenuTrigger>
								<DropdownMenuContent>
									<Can permissions="pricelist.delete">
										<DropdownMenuItem
											onClick={() => selfOnDelete(row.original)}
										>
											Delete
										</DropdownMenuItem>
									</Can>
								</DropdownMenuContent>
							</DropdownMenu>
						</Can>
					</div>
				),
			},
		],
		[pricelistID, selfOnDelete],
	);

	const formFilters: DataTableFiltersProp = useMemo(
		() => [
			{
				variant: "plain",
				layout: "flex",
				flex: {
					direction: "row",
					wrap: true,
				},
				fields: [
					{
						name: "pricelistPeriodName",
						label: "Name",
						type: "text",
					},
					{
						name: "status",
						label: "Status",
						labelPlacement: "stacked",
						defaultValue: null,
						type: "select",
						options: [
							{ label: "All", value: null },
							{ label: "Active", value: true },
							{ label: "Inactive", value: false },
						],
					},
				],
			},
		],
		[],
	);

	return (
		<>
			<DataTable<Partial<PricelistPeriod>, unknown>
				columns={columns}
				filterWrapper="none"
				filterShowActionsSeparator={false}
				formFilters={formFilters}
				onFormFilterChange={onFormFilterChange}
				data={data}
				loading={loading}
				showStandardActions={true}
				onRefresh={onRefresh}
				pagination
				paginationState={paginationState}
				onPaginationChange={onPaginationChange}
				rowCount={rowCount || undefined}
			/>
		</>
	);
};
